import {
  alpha,
  Box,
  FormHelperText,
  SxProps,
  Typography,
  useTheme,
} from '@mui/material';

import { useDevice } from '@/hooks/useDevice';

type CommonFormControlProps = {
  label?: string;
  secondLabel?: string;
  labelWidth?: number;
  error?: string;
  children?: React.ReactNode;
  sx?: SxProps;
  inLine?: boolean;
  highlightValue?: boolean;
  valueColor?: string;
  labelColor?: string;
  valueTextColor?: string;
};

export const CommonFormControl = ({
  label,
  secondLabel,
  labelWidth = 120,
  inLine,
  error,
  children,
  sx,
  highlightValue = false,
  valueColor,
  labelColor,
  valueTextColor,
}: CommonFormControlProps) => {
  const theme = useTheme();
  const { isMobile } = useDevice();

  // Check if the secondLabel is a currency value (starts with $)
  const isCurrencyValue =
    secondLabel &&
    typeof secondLabel === 'string' &&
    secondLabel.startsWith('$');

  // Convert color strings to proper MUI format
  const convertColor = (color: string) => {
    if (color === 'white') return '#ffffff';
    if (color === 'black') return '#000000';
    return color;
  };

  // Determine the color for the value
  const getValueColor = () => {
    if (valueTextColor) return convertColor(valueTextColor);
    if (valueColor) return convertColor(valueColor);
    if (highlightValue) return theme.palette.primary.main;
    if (isCurrencyValue) return theme.palette.success.main;
    return theme.palette.text.primary;
  };

  const valueColorFinal = getValueColor();
  const labelColorFinal = convertColor(
    labelColor || theme.palette.greyShades.grey1
  );

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          gap: '8px',
          alignItems: label ? 'flex-start' : 'center',
          flexWrap: 'wrap',
          ...(isMobile && !inLine && { flexDirection: 'column', gap: '4px' }),
          ...sx,
        }}
      >
        {label ? (
          <Typography
            sx={{
              width: `${labelWidth}px`,
              minHeight: '40px',
              color: labelColorFinal,
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              ...(isMobile && { minHeight: 'unset', flexShrink: 'unset' }),
              ...(isMobile && !inLine && { width: 'unset' }),
            }}
            variant="body1"
          >
            {label}
          </Typography>
        ) : null}
        {children}
        {secondLabel ? (
          <Typography
            sx={{
              minHeight: '40px',
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              whiteSpace: 'nowrap',
              ...(isMobile && { minHeight: 'unset' }),
              color: valueColorFinal,
              fontWeight: isCurrencyValue || highlightValue ? 700 : 400,
              fontSize: isCurrencyValue || highlightValue ? '1.1rem' : '1rem',
              ...(isCurrencyValue && {
                background: `linear-gradient(135deg, ${alpha(
                  valueColorFinal,
                  0.1
                )} 0%, ${alpha(valueColorFinal, 0.05)} 100%)`,
                px: 2,
                py: 0.5,
                borderRadius: 2,
                border: `1px solid ${alpha(valueColorFinal, 0.2)}`,
                textShadow: `0 1px 2px ${alpha(valueColorFinal, 0.2)}`,
              }),
              ...(highlightValue && {
                background: `linear-gradient(135deg, ${alpha(
                  valueColorFinal,
                  0.1
                )} 0%, ${alpha(valueColorFinal, 0.05)} 100%)`,
                px: 2,
                py: 0.5,
                borderRadius: 2,
                border: `1px solid ${alpha(valueColorFinal, 0.2)}`,
                textShadow: `0 1px 2px ${alpha(valueColorFinal, 0.2)}`,
              }),
            }}
            variant="body1"
          >
            {secondLabel}
          </Typography>
        ) : null}
      </Box>
      {error && (
        <Box
          sx={{
            mt: 1,
            ...(label &&
              (!isMobile || inLine) && { pl: `${labelWidth + 8}px` }),
          }}
        >
          <FormHelperText error>{error}</FormHelperText>
        </Box>
      )}
    </>
  );
};
